import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const DataSourceSelector = ({ selectedDataSources, onSourcesChange }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const dataSources = [
    {
      id: 1,
      name: 'Incident Database',
      type: 'incidents',
      description: 'All incident reports, near-misses, and investigation records',
      recordCount: 1247,
      lastUpdated: '2025-12-06',
      icon: 'AlertTriangle',
      color: 'text-red-500',
      status: 'active',
    },
    {
      id: 2,
      name: 'Training Records',
      type: 'training',
      description: 'Employee training completion, assessment scores, and certification status',
      recordCount: 3891,
      lastUpdated: '2025-12-05',
      icon: 'GraduationCap',
      color: 'text-blue-500',
      status: 'active',
    },
    {
      id: 3,
      name: 'Audit Findings',
      type: 'audits',
      description: 'Safety audit results, compliance findings, and corrective actions',
      recordCount: 456,
      lastUpdated: '2025-12-04',
      icon: 'ClipboardCheck',
      color: 'text-green-500',
      status: 'active',
    },
    {
      id: 4,
      name: 'Behavioral Observations',
      type: 'observations',
      description: 'Unsafe acts and conditions observations, behavioral safety data',
      recordCount: 2134,
      lastUpdated: '2025-12-06',
      icon: 'Eye',
      color: 'text-purple-500',
      status: 'active',
    },
    {
      id: 5,
      name: 'HAZID Worksheets',
      type: 'hazid',
      description: 'Hazard identification records, risk assessments, and control measures',
      recordCount: 789,
      lastUpdated: '2025-12-03',
      icon: 'AlertCircle',
      color: 'text-orange-500',
      status: 'active',
    },
    {
      id: 6,
      name: 'Compliance Documents',
      type: 'compliance',
      description: 'Statutory compliance records, permits, and regulatory documentation',
      recordCount: 234,
      lastUpdated: '2025-12-05',
      icon: 'Shield',
      color: 'text-indigo-500',
      status: 'active',
    },
    {
      id: 7,
      name: 'Work Permits',
      type: 'permits',
      description: 'Work permit records, hazard assessments, and approval workflows',
      recordCount: 1567,
      lastUpdated: '2025-12-06',
      icon: 'FileText',
      color: 'text-cyan-500',
      status: 'active',
    },
    {
      id: 8,
      name: 'External Databases',
      type: 'external',
      description: 'Industry benchmarks, regulatory updates, and external safety data',
      recordCount: 892,
      lastUpdated: '2025-12-01',
      icon: 'Database',
      color: 'text-pink-500',
      status: 'syncing',
    },
  ];

  const handleToggleSource = (source) => {
    const isSelected = selectedDataSources?.some(s => s?.id === source?.id);
    if (isSelected) {
      onSourcesChange(selectedDataSources?.filter(s => s?.id !== source?.id));
    } else {
      onSourcesChange([...selectedDataSources, source]);
    }
  };

  const filteredSources = dataSources?.filter(source =>
    source?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    source?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Data Sources</h3>
          <p className="text-sm text-muted-foreground">
            Select data sources to inform AI-generated content
          </p>
        </div>
        <div className="text-sm font-medium text-foreground">
          {selectedDataSources?.length} of {dataSources?.length} selected
        </div>
      </div>

      <div className="relative">
        <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e?.target?.value)}
          placeholder="Search data sources..."
          className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredSources?.map((source) => {
          const isSelected = selectedDataSources?.some(s => s?.id === source?.id);
          return (
            <div
              key={source?.id}
              onClick={() => handleToggleSource(source)}
              className={`bg-card border-2 rounded-lg p-6 cursor-pointer transition-all hover:shadow-md ${
                isSelected ? 'border-primary' : 'border-border'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg bg-muted/50 flex items-center justify-center ${source?.color}`}>
                  <Icon name={source?.icon} size={24} />
                </div>
                <div className="flex items-center gap-2">
                  {source?.status === 'syncing' && (
                    <div className="flex items-center gap-1 px-2 py-1 bg-yellow-500/10 text-yellow-600 rounded-lg text-xs font-medium">
                      <Icon name="RefreshCw" size={12} className="animate-spin" />
                      Syncing
                    </div>
                  )}
                  <div
                    className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                      isSelected
                        ? 'bg-primary border-primary' :'border-border'
                    }`}
                  >
                    {isSelected && <Icon name="Check" size={14} color="white" />}
                  </div>
                </div>
              </div>

              <h4 className="text-lg font-semibold text-foreground mb-2">{source?.name}</h4>
              <p className="text-sm text-muted-foreground mb-4">{source?.description}</p>

              <div className="flex items-center justify-between pt-4 border-t border-border">
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>{source?.recordCount?.toLocaleString()} records</span>
                  <span>|</span>
                  <span>Updated {source?.lastUpdated}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {selectedDataSources?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-foreground">Selected Data Sources</h4>
            <button
              onClick={() => onSourcesChange([])}
              className="text-sm text-destructive hover:text-destructive/80"
            >
              Clear All
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedDataSources?.map((source) => (
              <div
                key={source?.id}
                className="inline-flex items-center gap-2 px-3 py-2 bg-primary/10 text-primary rounded-lg text-sm"
              >
                <Icon name={source?.icon} size={14} />
                {source?.name}
                <button
                  onClick={(e) => {
                    e?.stopPropagation();
                    handleToggleSource(source);
                  }}
                  className="ml-1 hover:text-primary/80"
                >
                  <Icon name="X" size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataSourceSelector;